import AccountTreeIcon from "@mui/icons-material/AccountTree";
import AddIcon from "@mui/icons-material/Add";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BarChartIcon from "@mui/icons-material/BarChart";
import CasinoIcon from "@mui/icons-material/Casino";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import FilterListIcon from "@mui/icons-material/FilterList";
import FormatListBulleted from "@mui/icons-material/FormatListBulleted";
import GridView from "@mui/icons-material/GridView";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import LaunchIcon from "@mui/icons-material/Launch";
import ListAltIcon from "@mui/icons-material/ListAlt";
import SearchIcon from "@mui/icons-material/Search";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
	Alert,
	Box,
	Card,
	CardActions,
	CardContent,
	CardHeader,
	Chip,
	IconButton,
	Menu,
	MenuItem,
	MenuList,
	Snackbar,
	Stack,
	Tooltip,
	useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import {
	GaugeContainer,
	GaugeReferenceArc,
	GaugeValueArc,
	useGaugeState,
} from "@mui/x-charts/Gauge";
import { isPast } from "date-fns";
import { parseAsInteger, parseAsString, useQueryState } from "nuqs";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link as RRLink, useNavigate } from "react-router-dom";
import {
	Assignment,
	AssignmentStatus,
	cloneAssignment,
	createAssignment,
	DEFAULT_ASSIGNMENT_STATUS_FILTER,
	deleteAssignment,
	getAssignmentById,
	getAssignments,
	getRandomDescription,
	patchAssignmentById,
	statusKeysToValues,
	statusToThemeKey,
	User,
} from "../../api";
import {
	AssignmentLlmQueueStat,
	getAssignmentLlmQueueStats,
} from "../../api/assignments/llm-queue-stats";
import {
	AssignmentVoteCounts,
	getAssignmentVoteCountsBulk,
} from "../../api/assignments/vote-counts";
import {
	CelTask,
	filterRelevantTasks,
	getRunningTasksByAssignment,
} from "../../api/celtask";
import {
	CommsMatrixStats,
	getCommsMatrixStatsBulk,
} from "../../api/statistics/communicationmatrix";
import { useRefreshHotkey } from "../../hooks/use-refresh-hotkey";
import { useStatusChoices } from "../../hooks/use-status-choices";
import { getTaskDisplayInfo } from "../../utils/celery-task-display";
import { getDisplayCompanyName } from "../../utils/privacy";
import AssignmentAddDialog from "../assignmentsv2/AssignmentAddDialog";
import AssignmentsList from "../assignmentsv2/AssignmentsList";
import AssignmentViewModal from "../assignmentsv2/AssignmentViewModal";
import { uniqueName } from "../assignmentsv2/util";
import { useGlobalState } from "../globalState";

const GRADE_METHOD_OPTIONS: { key: string; label: string }[] = [
	{ key: "ml", label: "ML" },
	{ key: "search", label: "SEARCH" },
	{ key: "llm_yes", label: "LLM YES" },
	{ key: "llm_no", label: "LLM NO" },
];

const GaugePointer = () => {
	const theme = useTheme();
	const { valueAngle, outerRadius, cx, cy } = useGaugeState();

	if (valueAngle === null) {
		return null;
	}

	const target = {
		x: cx + outerRadius * Math.sin(valueAngle),
		y: cy - outerRadius * Math.cos(valueAngle),
	};
	return (
		<g>
			<circle cx={cx} cy={cy} r={3} fill={theme.actionButtons.thumbDown.main} />
			<path
				d={`M ${cx} ${cy} L ${target.x} ${target.y}`}
				stroke={theme.actionButtons.thumbDown.main}
				strokeWidth={2}
			/>
		</g>
	);
};

const KpiBadge = ({
	value,
	tooltipTitle,
	tooltipBody,
	onClick,
	to,
}: {
	value: number;
	tooltipTitle: string;
	tooltipBody?: React.ReactNode;
	onClick?: () => void;
	to?: string;
}) => {
	function formatNumber(num: number): string {
		if (num === undefined || num === null) return "N/A";
		if (num < 1000) {
			return num.toString(); // Less than 1000, return the number as is
		} else if (num < 1000000) {
			return (Math.round(num / 100) / 10).toFixed(0) + "K"; // Thousands
		} else {
			return (Math.round(num / 100000) / 10).toFixed(0) + "M"; // Millions
		}
	}

	return (
		<Tooltip
			data-component="kpi-tooltip"
			title={
				value !== undefined && value !== null ? (
					<Stack spacing={2} data-component="tooltip-content-stack">
						<Typography data-component="tooltip-title">
							{tooltipTitle} ({value})
						</Typography>
						{tooltipBody}
					</Stack>
				) : (
					<Typography data-component="tooltip-loading">
						{tooltipTitle} Loading...
					</Typography>
				)
			}
		>
			<Stack spacing={1} alignItems="center" data-component="kpi-badge-stack">
				<Chip
					data-component="kpi-chip"
					sx={{
						background: (theme) => theme.palette.background.paper,
						cursor: onClick || to ? "pointer" : undefined,
					}}
					size="small"
					clickable={Boolean(onClick || to)}
					onClick={onClick}
					{...(to ? { component: RRLink, to } : {})}
					label={
						value !== undefined && value !== null ? (
							<Typography
								data-component="kpi-value"
								variant="body2"
								fontWeight="bold"
								color="primary"
							>
								{formatNumber(value)}
							</Typography>
						) : (
							<Typography
								data-component="kpi-placeholder"
								variant="body2"
								fontWeight="bold"
								sx={{
									color: (theme) => theme.palette.background.paper,
								}}
							>
								{0}
							</Typography>
						)
					}
				/>
			</Stack>
		</Tooltip>
	);
};

type RunningTasksMap = Record<number, CelTask[]>;

const TaskIcon = ({ taskName }: { taskName: string }) => {
	const info = getTaskDisplayInfo(taskName);
	const iconStyle = { fontSize: 16 };
	switch (info.icon) {
		case "Search":
			return <SearchIcon sx={iconStyle} />;
		case "AutoAwesome":
			return <AutoAwesomeIcon sx={iconStyle} />;
		default:
			return <HourglassBottomIcon sx={iconStyle} />;
	}
};

const AssignmentComponent = ({
	readOnly,
	assignment,
	activeUser,
	onSelectAssignment,
	runningTasks = null,
	llmQueueCount = 0,
	commsMatrixStats,
	voteCounts,
}: {
	readOnly: boolean;
	assignment: Assignment;
	activeUser: User;
	onSelectAssignment?: (assignment: Assignment) => void;
	runningTasks?: RunningTasksMap | null;
	llmQueueCount?: number;
	commsMatrixStats?: CommsMatrixStats;
	voteCounts?: AssignmentVoteCounts;
}) => {
	const { currentUser, users } = useGlobalState();
	const navigate = useNavigate();
	const stats: CommsMatrixStats = commsMatrixStats ?? {
		sent: null,
		responses: null,
		interested: null,
	};
	const [assignmentWarning, setAssignmentWarning] = useState<{
		text: string;
		icon: React.ReactElement<any, string | React.JSXElementConstructor<any>>;
	}>({ text: "", icon: null });

	const [thumbMenuPosition, setThumbMenuPosition] = useState<{
		mouseX: number;
		mouseY: number;
	} | null>(null);

	const handleOpenThumbContextMenu = (event: React.MouseEvent) => {
		event.preventDefault();
		setThumbMenuPosition({
			mouseX: event.clientX + 2,
			mouseY: event.clientY - 6,
		});
	};

	const handleCloseThumbContextMenu = () => setThumbMenuPosition(null);

	const handleSelectGradeMethod = (method: string) => {
		navigate(`/grading/${assignment.id}?method=${method}`);
		setThumbMenuPosition(null);
	};

	useEffect(() => {
		validateCard();
	}, [assignment]);

	const validateCard = () => {
		if (!assignment.submission_deadline) return;

		let text = "";
		let icon = null;
		const twoDaysInSeconds = 86400000 * 2;

		if (isPast(new Date(assignment.submission_deadline))) {
			text = "Submission deadline surpassed";
			icon = <ErrorOutlineIcon color={"error"} />;
		} else if (
			new Date(new Date().getTime() + twoDaysInSeconds) >=
			new Date(assignment.submission_deadline)
		) {
			text = "Submission deadline is soon";
			icon = <WarningAmberIcon color={"warning"} />;
		}
		setAssignmentWarning({ text, icon });
	};

	const secondaryValue = () => {
		const href = `/grading/${assignment.id}?method=llm`;
		const board_href = `/board/v2?assignmentId=${assignment.id}`;
		const stats_href = `/assignment_stats/${assignment.id}`;

		return (
			<>
				{assignment.users.includes(currentUser.id) ||
				assignment.thumbers.includes(currentUser.id) ||
				currentUser.is_staff ? (
					<Tooltip title="Vote" data-component="vote-tooltip">
						<IconButton
							data-component="vote-button"
							color="primary"
							size="small"
							component={RRLink}
							to={href}
							onContextMenu={handleOpenThumbContextMenu}
						>
							<ThumbUpOffAltIcon />
						</IconButton>
					</Tooltip>
				) : null}

				<Tooltip title="Board" data-component="board-tooltip">
					<IconButton
						data-component="board-button"
						size="small"
						component={RRLink}
						to={board_href}
						color="primary"
					>
						<ListAltIcon />
					</IconButton>
				</Tooltip>
				<Tooltip title="Pipeline" data-component="pipeline-tooltip">
					<IconButton
						data-component="pipeline-button"
						size="small"
						component={RRLink}
						to={`/funnel/assignment/${assignment.id}`}
						color="primary"
					>
						<FilterListIcon />
					</IconButton>
				</Tooltip>
				{currentUser.is_superuser || currentUser.is_staff ? (
					<Tooltip title="Statistics" data-component="stats-tooltip">
						<IconButton
							data-component="stats-button"
							size="small"
							component={RRLink}
							to={stats_href}
							color="primary"
						>
							<BarChartIcon />
						</IconButton>
					</Tooltip>
				) : null}
				{currentUser.is_staff && (
					<Tooltip
						title="Candidates Pipeline"
						data-component="candidates-tooltip"
					>
						<IconButton
							data-component="candidates-button"
							size="small"
							component={RRLink}
							to={`/candidates/${assignment.id}`}
							color="primary"
						>
							<AccountTreeIcon />
						</IconButton>
					</Tooltip>
				)}
			</>
		);
	};

	const statsSection = () => {
		return (
			<>
				<Grid data-component="votes-grid">
					<KpiBadge
						value={voteCounts?.total}
						tooltipTitle="Number of Votes"
						to={`/funnel/assignment/${assignment.id}`}
					/>
				</Grid>
				<Grid data-component="upvotes-grid">
					<KpiBadge
						value={voteCounts?.up}
						tooltipTitle="Number of Upvotes"
						to={`/funnel/assignment/${assignment.id}?filter=upvotes`}
					/>
				</Grid>
				<Grid data-component="sent-grid">
					<KpiBadge
						value={stats.sent}
						tooltipTitle="Sent Messages"
						to={`/funnel/assignment/${assignment.id}?filter=sent`}
					/>
				</Grid>
				<Grid data-component="responses-grid">
					<KpiBadge
						value={stats.responses}
						tooltipTitle="Received Answers"
						to={`/funnel/assignment/${assignment.id}?filter=responses`}
					/>
				</Grid>
				<Grid data-component="interested-grid">
					<KpiBadge
						value={stats.interested}
						tooltipTitle="Interested candidates"
						to={`/funnel/assignment/${assignment.id}?filter=interested`}
					/>
				</Grid>
			</>
		);
	};

	const theme = useTheme();
	let cardTitle;
	let cardSubheader;

	if (assignment.is_recruiting_company) {
		cardTitle = (
			<Stack data-component="company-title-stack">
				<Typography color="text.primary" data-component="company-name">
					{assignment.company != null
						? getDisplayCompanyName(
								assignment.company_name,
								assignment.company,
								currentUser.userprofile
							)
						: ""}
				</Typography>
			</Stack>
		);
		cardSubheader = (
			<Link
				data-component="assignment-link"
				onClick={() => onSelectAssignment(assignment)}
				sx={{ cursor: "pointer" }}
			>
				<Typography
					variant="body2"
					color="text.primary"
					data-component="assignment-name"
				>
					{assignment.name} ({assignment.id})
				</Typography>
			</Link>
		);
	} else {
		cardTitle = (
			<Link
				data-component="assignment-title-link"
				onClick={() => onSelectAssignment(assignment)}
				sx={{ cursor: "pointer" }}
			>
				<Typography color="text.primary" data-component="assignment-title">
					{assignment.name} ({assignment.id})
				</Typography>
			</Link>
		);
		cardSubheader = null;
	}

	return (
		<Box data-component="assignment-grid" sx={{ display: "grid" }}>
			<Card
				data-component="assignment-card"
				data-assignment-id={assignment.id}
				sx={{
					backgroundColor: theme.palette.background.default,
					position: "relative",
				}}
			>
				<CardHeader
					data-component="assignment-header"
					sx={{ p: 1 }}
					title={cardTitle}
					subheader={cardSubheader}
					action={
						assignment.external_url ? (
							<Tooltip
								title="External link"
								data-component="external-link-tooltip"
							>
								<IconButton
									data-component="external-link-button"
									size="small"
									href={assignment.external_url}
									target="_blank"
								>
									<LaunchIcon />
								</IconButton>
							</Tooltip>
						) : null
					}
				/>
				<CardContent sx={{ p: 1 }} data-component="assignment-content">
					<Grid
						container
						spacing={1}
						alignItems="center"
						data-component="assignment-details"
					>
						<Grid data-component="status-grid">
							<Chip
								data-component="status-chip"
								label={assignment.status_display}
								size="small"
								sx={{
									backgroundColor:
										theme.assignmentStatus[
											statusToThemeKey[
												AssignmentStatus[assignment.status]
											]
										],
									color: "common.black",
								}}
							/>
							{!assignment.users.includes(currentUser.id) &&
								assignment.thumbers.includes(currentUser.id) && (
									<Tooltip
										title={`Screener only — outreach is handled by ${
											assignment.users
												.map(
													(id) =>
														users?.find((u) => u.id === id)
															?.first_name
												)
												.filter(Boolean)
												.join(", ") || "unknown"
										}`}
									>
										<Chip
											data-component="screener-chip"
											label="Screener"
											size="small"
											color="info"
											variant="outlined"
											sx={{ ml: 0.5 }}
										/>
									</Tooltip>
								)}
							{assignment.egenkonsult && (
								<Chip
									data-component="assignment-card-freelancer"
									label="Freelancer"
									size="small"
									color="secondary"
									variant="filled"
									sx={{ ml: 0.5 }}
								/>
							)}
						</Grid>
						<Grid data-component="celery-grid">
							{(() => {
								const allTasks = runningTasks?.[assignment.id];
								if (!allTasks || allTasks.length === 0) return null;

								const relevantTasks = filterRelevantTasks(allTasks);
								if (relevantTasks.length === 0) return null;

								return (
									<Tooltip
										data-component="celery-tooltip"
										title={
											<Stack
												spacing={1}
												data-component="celery-stack"
											>
												{relevantTasks.map((task) => {
													const displayInfo =
														getTaskDisplayInfo(
															task.task_name
														);
													return (
														<Stack
															key={task.task_id}
															direction="row"
															spacing={1}
															alignItems="center"
															data-component="celery-task"
														>
															<TaskIcon
																taskName={
																	task.task_name
																}
															/>
															<Typography
																variant="body2"
																data-component="task-label"
															>
																{displayInfo.label}
															</Typography>
														</Stack>
													);
												})}
											</Stack>
										}
									>
										<Chip
											data-component="updating-chip"
											icon={
												<HourglassBottomIcon
													style={{
														color: theme.status.updating,
													}}
												/>
											}
											label="Updating"
											size="small"
											sx={{
												backgroundColor: (theme) =>
													theme.status.info,
												color: "common.black",
											}}
										/>
									</Tooltip>
								);
							})()}
						</Grid>
						<Grid data-component="warning-grid">
							{assignmentWarning.text !== "" ? (
								<Tooltip
									data-component="warning-tooltip"
									title={`${assignmentWarning.text}: ${assignment.submission_deadline}`}
								>
									<IconButton
										data-component="warning-button"
										size="small"
										onClick={() => onSelectAssignment(assignment)}
									>
										{assignmentWarning.icon}
									</IconButton>
								</Tooltip>
							) : null}
						</Grid>
						<Grid size={12} data-component="stats-container">
							<Grid
								container
								spacing={1}
								mt={2}
								data-component="stats-grid"
							>
								{statsSection()}
							</Grid>
						</Grid>
					</Grid>
				</CardContent>
				<CardActions data-component="card-actions">
					{secondaryValue()}
				</CardActions>
				<Tooltip title="Candidates in Queue" data-component="llm-gauge-tooltip">
					<Box
						data-component="llm-gauge-container"
						sx={{
							position: "absolute",
							bottom: 4,
							right: 4,
							width: 60,
							height: 60,
						}}
					>
						<GaugeContainer
							width={60}
							height={60}
							startAngle={-110}
							endAngle={110}
							value={Math.min(llmQueueCount, 100)}
							valueMin={0}
							valueMax={100}
						>
							<GaugeReferenceArc />
							<GaugeValueArc />
							<GaugePointer />
						</GaugeContainer>
					</Box>
				</Tooltip>
				<Menu
					data-component="context-menu"
					open={thumbMenuPosition !== null}
					onClose={handleCloseThumbContextMenu}
					anchorReference="anchorPosition"
					anchorPosition={
						thumbMenuPosition !== null
							? {
									top: thumbMenuPosition.mouseY,
									left: thumbMenuPosition.mouseX,
								}
							: undefined
					}
				>
					<MenuList dense data-component="menu-list">
						{GRADE_METHOD_OPTIONS.map((opt) => (
							<MenuItem
								data-component="grade-method-item"
								key={`grade-method-${opt.key}`}
								onClick={() => handleSelectGradeMethod(opt.key)}
							>
								{opt.label}
							</MenuItem>
						))}
					</MenuList>
				</Menu>
			</Card>
		</Box>
	);
};

const RecruitmentAssignmentList = ({
	assignments,
	readOnly = false,
	activeUser,
	onSelectAssignment,
	onAddAssignment,
	onAddRandomAssignment,
	runningTasks = null,
	llmQueueStats = [],
	commsMatrixStatsMap = {},
	voteCountsMap = {},
}: {
	assignments: Assignment[];
	connected?: boolean;
	readOnly?: boolean;
	activeUser: User;
	onSelectAssignment?: (assignment: Assignment) => void;
	onAddAssignment?: () => void;
	onAddRandomAssignment?: () => void;
	runningTasks?: RunningTasksMap | null;
	llmQueueStats?: AssignmentLlmQueueStat[];
	commsMatrixStatsMap?: Record<number, CommsMatrixStats>;
	voteCountsMap?: Record<number, AssignmentVoteCounts>;
}) => {
	const theme = useTheme();
	const llmQueueMap = llmQueueStats.reduce(
		(acc, stat) => {
			acc[stat.assignment_id] = stat.llm_queue_count;
			return acc;
		},
		{} as Record<number, number>
	);

	return (
		<Box
			sx={{
				display: "grid",
				gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
				gap: 2,
				pb: 2,
				px: 2,
			}}
			data-component="assignment-list"
		>
			{onAddAssignment && (
				<Box data-component="add-assignment-grid" sx={{ display: "grid" }}>
					<Card
						data-component="add-assignment-card"
						onClick={onAddAssignment}
						sx={{
							border: `2px dashed ${theme.palette.divider}`,
							backgroundColor: "transparent",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							cursor: "pointer",
							minHeight: 180,
							"&:hover": {
								borderColor: theme.palette.primary.main,
								backgroundColor: theme.palette.action.hover,
							},
						}}
					>
						<Stack alignItems="center" spacing={1}>
							<AddIcon
								sx={{
									fontSize: 40,
									color: theme.palette.text.secondary,
								}}
							/>
							<Typography variant="body2" color="text.secondary">
								Add new Role
							</Typography>
						</Stack>
					</Card>
				</Box>
			)}
			{onAddRandomAssignment &&
				(activeUser.is_staff || activeUser.is_superuser) && (
					<Box
						data-component="add-random-assignment-grid"
						sx={{ display: "grid" }}
					>
						<Card
							data-component="add-random-role-card"
							id="assignments-add-random-role-button"
							onClick={onAddRandomAssignment}
							sx={{
								border: `2px dashed ${theme.palette.divider}`,
								backgroundColor: "transparent",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								cursor: "pointer",
								minHeight: 180,
								"&:hover": {
									borderColor: theme.palette.primary.main,
									backgroundColor: theme.palette.action.hover,
								},
							}}
						>
							<Stack alignItems="center" spacing={1}>
								<CasinoIcon
									sx={{
										fontSize: 40,
										color: theme.palette.text.secondary,
									}}
								/>
								<Typography variant="body2" color="text.secondary">
									Add random Role
								</Typography>
							</Stack>
						</Card>
					</Box>
				)}
			{assignments.map((assignment) => {
				return (
					<AssignmentComponent
						key={assignment.id}
						readOnly={readOnly}
						activeUser={activeUser}
						assignment={assignment}
						runningTasks={runningTasks}
						onSelectAssignment={onSelectAssignment}
						llmQueueCount={llmQueueMap[assignment.id] ?? 0}
						commsMatrixStats={commsMatrixStatsMap[assignment.id]}
						voteCounts={voteCountsMap[assignment.id]}
					/>
				);
			})}
		</Box>
	);
};

export const RecruitAssignments = ({
	title,
	activeUser,
	readOnly = false,
}: {
	title: React.ReactNode;
	activeUser: User;
	readOnly?: boolean;
}) => {
	const [statusFilter, setStatusFilter] = useQueryState(
		"statusFilter",
		parseAsString.withDefault(DEFAULT_ASSIGNMENT_STATUS_FILTER)
	);
	const [assignmentIdParam, setAssignmentIdParam] = useQueryState(
		"assignmentId",
		parseAsInteger
	);
	const [viewType, setViewType] = useQueryState(
		"viewtype",
		parseAsString.withDefault("card")
	);
	const [assignments, setAssignments] = useState<Assignment[]>([]);
	const [runningTasks, setRunningTasks] = useState<RunningTasksMap | null>(null);
	const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(
		null
	);
	const [snackbarMessage, setSnackbarMessage] = useState<string>("");
	const [llmQueueStats, setLlmQueueStats] = useState<AssignmentLlmQueueStat[]>([]);
	const [commsMatrixStatsMap, setCommsMatrixStatsMap] = useState<
		Record<number, CommsMatrixStats>
	>({});
	const [voteCountsMap, setVoteCountsMap] = useState<
		Record<number, AssignmentVoteCounts>
	>({});
	const [openAddDialog, setOpenAddDialog] = useState(false);
	const [prefillDescription, setPrefillDescription] = useState<string | null>(null);
	const fetchIdRef = useRef(0);

	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const open = Boolean(anchorEl);
	const id = open ? "filter-menu" : undefined;
	const theme = useTheme();
	const statusChoices = useStatusChoices();

	const activeStatuses = statusFilter ? statusFilter.split(",") : [];

	useEffect(() => {
		if (activeStatuses.length === 0) {
			setAssignments([]);
			return;
		}

		const statusValues = statusKeysToValues(activeStatuses);
		if (statusValues.length === 0) {
			setAssignments([]);
			return;
		}

		const requestId = ++fetchIdRef.current;

		Promise.all([
			getAssignments(undefined, { statusValues, userId: activeUser.id }),
			getRunningTasksByAssignment([
				"core.tasks.rebuild_gcq",
				"core.tasks.fill_llm_queue",
			]),
		]).then(([assignmentsRes, tasksRes]) => {
			if (requestId !== fetchIdRef.current) return;

			// Sort by company_name with id as stable tiebreaker
			const sorted = [...assignmentsRes].sort((a, b) => {
				const nameA = a.company_name ?? "";
				const nameB = b.company_name ?? "";
				if (nameA !== nameB) return nameA > nameB ? 1 : -1;
				return a.id - b.id;
			});
			setAssignments(sorted);
			setRunningTasks(tasksRes);

			if (assignmentIdParam !== null) {
				const found = sorted.find((el) => el.id === assignmentIdParam);
				if (found) setSelectedAssignment(found);
			}
		});
	}, [statusFilter, activeUser.id]);

	const fetchLlmQueueStats = useCallback(() => {
		if (assignments.length === 0) return;
		const assignmentIds = assignments.map((a) => a.id);
		getAssignmentLlmQueueStats(assignmentIds).then(setLlmQueueStats);
	}, [assignments]);

	useEffect(() => {
		if (assignments.length === 0) {
			setLlmQueueStats([]);
			return;
		}
		fetchLlmQueueStats();
	}, [fetchLlmQueueStats]);

	useEffect(() => {
		if (assignments.length === 0) {
			setCommsMatrixStatsMap({});
			setVoteCountsMap({});
			return;
		}
		const ids = assignments.map((a) => a.id);
		getCommsMatrixStatsBulk(ids).then(setCommsMatrixStatsMap);
		getAssignmentVoteCountsBulk(ids).then(setVoteCountsMap);
	}, [assignments]);

	const fetchAssignmentTasks = () => {
		getRunningTasksByAssignment([
			"core.tasks.rebuild_gcq",
			"core.tasks.fill_llm_queue",
		]).then(setRunningTasks);
	};

	useEffect(() => {
		const interval = setInterval(() => {
			fetchAssignmentTasks();
			fetchLlmQueueStats();
		}, 60 * 1000);
		return () => clearInterval(interval);
	}, [fetchLlmQueueStats]);

	useRefreshHotkey(
		useCallback(() => {
			fetchAssignmentTasks();
			fetchLlmQueueStats();
		}, [fetchLlmQueueStats])
	);

	const handleFilter = (status: string) => {
		const current = activeStatuses;
		let newStatuses: string[];
		if (current.includes(status)) {
			newStatuses = current.filter((st) => st !== status);
		} else {
			newStatuses = [...current, status];
		}
		setStatusFilter(newStatuses.length > 0 ? newStatuses.join(",") : null);
	};

	const fetchLatestAssignment = (assignment: Assignment) => {
		//Fetch new assignment before editing
		getAssignmentById(assignment.id).then((ass) => {
			setAssignments((prev) => {
				return [...prev].map((el) => {
					if (el.id === ass.id) {
						return ass;
					}
					return el;
				});
			});
			setSelectedAssignment(ass);
		});
	};

	const onSelectAssignment = (assignment: Assignment) => {
		fetchLatestAssignment(assignment);
		setAssignmentIdParam(assignment.id);
	};

	const handleCloseAssignment = () => {
		setAssignmentIdParam(null);
		setSelectedAssignment(null);
		fetchAssignmentTasks();
	};

	const handleUpdateAssignment = (assignment: Assignment) => {
		console.debug({ assignment });

		return patchAssignmentById(assignment.id, assignment).then((res) => {
			setAssignments((prev) =>
				[...prev].map((assign) => {
					if (res.id === assign.id) {
						return res;
					}
					return assign;
				})
			);
			return res;
		});
	};

	const handleAddNewAssignment = (assignment: Assignment) => {
		return createAssignment({ ...assignment }).then((res) => {
			setAssignments((prev) => [...prev, res]);
			setOpenAddDialog(false);
			setPrefillDescription(null);
			return res;
		});
	};

	const handleAddRandomAssignment = async () => {
		const response = await getRandomDescription();
		setPrefillDescription(`${response.description}\n<!-- test-${Date.now()} -->`);
		setOpenAddDialog(true);
	};

	const handleCloneAssignment = async (assignment: Assignment) => {
		const newAssignmentName = uniqueName(
			assignment.name,
			assignments.map((el) => el.name)
		);
		const clonedAssignment = await cloneAssignment({
			...assignment,
			name: newAssignmentName,
		});
		setAssignments((prev) => [clonedAssignment, ...prev]);
	};

	const handleDeleteAssignment = (assignment: Assignment) => {
		deleteAssignment(assignment.id).then(() => {
			setAssignments((prev) => prev.filter((el) => el.id !== assignment.id));
		});
	};

	const handleGoToBoard = (assignment: Assignment) => {
		window.open(`board/v2?assignmentId=${assignment.id}`, "_blank");
	};

	const handleOpenFilterMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};
	const handleCloseFilterMenu = () => {
		setAnchorEl(null);
	};

	return (
		<>
			<Grid size={12} data-component="main-container">
				<Grid container spacing={1} data-component="content-wrapper">
					<Grid size={12} data-component="card-container">
						<Card data-component="main-card">
							<Stack
								data-component="header-stack"
								direction="row"
								alignItems="center"
								justifyContent="space-between"
								p={2}
							>
								<Typography variant="h6" data-component="title">
									{title}
								</Typography>
								<Stack direction="row" data-component="header-actions">
									<IconButton
										data-component="view-card-button"
										disabled={viewType === "card"}
										onClick={() => setViewType("card")}
									>
										<GridView />
									</IconButton>
									<IconButton
										data-component="view-list-button"
										disabled={viewType === "list"}
										onClick={() => setViewType("list")}
									>
										<FormatListBulleted />
									</IconButton>
									<IconButton
										onClick={handleOpenFilterMenu}
										data-component="filter-button"
									>
										{<FilterListIcon />}
									</IconButton>
								</Stack>
								<Menu
									data-component="filter-menu"
									id={id}
									open={open}
									anchorEl={anchorEl}
									onClose={handleCloseFilterMenu}
									anchorOrigin={{
										vertical: "bottom",
										horizontal: "left",
									}}
								>
									<Box p={2} data-component="filter-content">
										<Typography data-component="filter-title">
											Filter Status
										</Typography>
										<MenuList
											sx={{ maxWidth: 250 }}
											data-component="filter-menu-list"
										>
											<Grid
												container
												spacing={1}
												data-component="filter-chips-grid"
											>
												{statusChoices.map((choice) => {
													const isActive =
														activeStatuses.includes(
															choice.key
														);
													const chipColor =
														theme.assignmentStatus[
															statusToThemeKey[choice.key]
														];
													return (
														<Grid
															data-component="filter-chip-grid"
															key={`filter-button-${choice.key}`}
														>
															<Chip
																data-component="filter-chip"
																label={choice.name}
																sx={{
																	backgroundColor:
																		isActive
																			? chipColor
																			: null,
																	color: isActive
																		? "common.black"
																		: null,
																	"&.MuiChip-root:hover":
																		{
																			backgroundColor:
																				isActive
																					? chipColor
																					: null,
																			color: isActive
																				? "common.black"
																				: null,
																		},
																}}
																size="small"
																variant="outlined"
																onClick={() =>
																	handleFilter(
																		choice.key
																	)
																}
															/>
														</Grid>
													);
												})}
											</Grid>
										</MenuList>
									</Box>
								</Menu>
							</Stack>
							{viewType === "list" ? (
								<Box p={2}>
									<AssignmentsList
										id="todo-assignments-list"
										assignments={assignments}
										onClickAssignment={onSelectAssignment}
										onCloneAssignment={handleCloneAssignment}
										onDeleteAssignment={handleDeleteAssignment}
										onGoToBoard={handleGoToBoard}
									/>
								</Box>
							) : (
								<RecruitmentAssignmentList
									assignments={assignments}
									readOnly={readOnly}
									activeUser={activeUser}
									runningTasks={runningTasks}
									onSelectAssignment={onSelectAssignment}
									onAddAssignment={() => setOpenAddDialog(true)}
									onAddRandomAssignment={handleAddRandomAssignment}
									llmQueueStats={llmQueueStats}
									commsMatrixStatsMap={commsMatrixStatsMap}
									voteCountsMap={voteCountsMap}
								/>
							)}
						</Card>
					</Grid>
				</Grid>
			</Grid>
			{selectedAssignment !== null ? (
				<AssignmentViewModal
					open={selectedAssignment !== null}
					assignment={selectedAssignment}
					assignments={assignments}
					onSave={(assignment) => handleUpdateAssignment(assignment)}
					onClose={handleCloseAssignment}
				/>
			) : null}
			<AssignmentAddDialog
				open={openAddDialog}
				onClose={() => {
					setOpenAddDialog(false);
					setPrefillDescription(null);
				}}
				onSave={(data) => handleAddNewAssignment(data)}
				initialDescription={prefillDescription ?? undefined}
			/>
			<Snackbar
				data-component="notification-snackbar"
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
				open={snackbarMessage !== ""}
				onClose={() => setSnackbarMessage("")}
				autoHideDuration={6000}
			>
				<Alert
					data-component="notification-alert"
					onClose={() => setSnackbarMessage("")}
					severity="success"
					sx={{ width: "100%" }}
				>
					{snackbarMessage}
				</Alert>
			</Snackbar>
		</>
	);
};
